import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

// Hook that triggers when element enters viewport
const useReveal = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.2, ...options }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return [ref, isVisible];
};

const DoctorRow = ({ name, title, description1, description2, image, alt, buttonText, reversed }) => {
  const [rowRef, isVisible] = useReveal();

  return (
    <div ref={rowRef} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      {/* Image */}
      <div className={`relative w-full max-w-md mx-auto ${reversed ? "order-1 lg:order-2" : ""}`}>
        {/* Decorative accent behind image */}
        <div
          className={`absolute -z-10 rounded-[32px] bg-gradient-to-br ${reversed ? "from-[#E09F3E]/20 to-[#0F4C5C]/10 dark:from-[#E09F3E]/10 dark:to-transparent" : "from-[#0F4C5C]/10 to-[#E09F3E]/20 dark:from-transparent dark:to-[#E09F3E]/10"} transition-all duration-1000 ease-out ${reversed ? "-right-4 -bottom-4" : "-left-4 -bottom-4"}`}
          style={{
            width: isVisible ? "100%" : "0%",
            height: isVisible ? "100%" : "0%",
            transitionDelay: "0.3s",
          }}
        />
        {/* Image with clip-path reveal */}
        <div
          className="aspect-[4/5] overflow-hidden rounded-[32px] border border-gray-100 dark:border-white/10 shadow-2xl bg-gray-50 dark:bg-slate-900"
          style={{
            clipPath: isVisible
              ? "inset(0% 0% 0% 0%)"
              : reversed
                ? "inset(0% 0% 0% 100%)"
                : "inset(0% 100% 0% 0%)",
            transition: "clip-path 1s cubic-bezier(0.77, 0, 0.175, 1)",
          }}
        >
          <img
            src={image}
            alt={alt}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out"
            style={{
              transform: isVisible ? "scale(1)" : "scale(1.15)",
              filter: isVisible ? "blur(0px)" : "blur(4px)",
              transition: "transform 1.4s cubic-bezier(0.16, 1, 0.3, 1), filter 1.2s ease-out",
            }}
          />
        </div>
      </div>

      {/* Text */}
      <div className={`flex flex-col justify-center text-left ${reversed ? "order-2 lg:order-1" : ""}`}>
        {/* Name */}
        <div className="overflow-hidden">
          <h3
            className="text-3xl md:text-4xl font-serif font-medium text-[#0F4C5C] dark:text-[#E09F3E] mb-2"
            style={{
              transform: isVisible ? "translateY(0)" : "translateY(100%)",
              opacity: isVisible ? 1 : 0,
              transition: "transform 0.7s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.7s ease",
              transitionDelay: "0.3s",
            }}
          >
            {name}
          </h3>
        </div>

        {/* Title with animated underline */}
        <div className="mb-6">
          <p
            className="text-[#E09F3E] dark:text-[#56b255] font-bold text-sm uppercase tracking-wider"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
              transitionDelay: "0.45s",
            }}
          >
            {title}
          </p>
          <div
            className="h-[2px] bg-gradient-to-r from-[#E09F3E] to-transparent mt-3"
            style={{
              width: isVisible ? "80px" : "0px",
              transition: "width 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
              transitionDelay: "0.6s",
            }}
          />
        </div>

        {/* Paragraph 1 */}
        <p
          className="text-gray-600 dark:text-slate-300 text-base md:text-lg leading-relaxed mb-6"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(30px)",
            transition: "all 0.7s cubic-bezier(0.16, 1, 0.3, 1)",
            transitionDelay: "0.55s",
          }}
        >
          {description1}
        </p>

        {/* Paragraph 2 */}
        <p
          className="text-gray-600 dark:text-slate-300 text-base md:text-lg leading-relaxed mb-8"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(30px)",
            transition: "all 0.7s cubic-bezier(0.16, 1, 0.3, 1)",
            transitionDelay: "0.65s",
          }}
        >
          {description2}
        </p>

        {/* Button */}
        <div
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0) scale(1)" : "translateY(20px) scale(0.95)",
            transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
            transitionDelay: "0.8s",
          }}
        >
          <Link to="/contact" className="inline-block px-8 py-4 bg-[#56b255] hover:bg-[#469e45] text-white font-bold rounded-full shadow-md hover:shadow-lg transition-all duration-300">
            {buttonText}
          </Link>
        </div>
      </div>
    </div>
  );
};

const AboutUs = () => {
  const { t } = useLanguage();
  const [headerRef, headerVisible] = useReveal();

  return (
    <section id="about" className="py-24 bg-gradient-to-br from-white via-blue-50/30 to-white dark:from-[#051518] dark:via-[#072227]/30 dark:to-[#051518] overflow-hidden transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-24">
        
        {/* Section Header */}
        <div ref={headerRef} className="text-center max-w-3xl mx-auto">
          {/* Badge */}
          <p
            className="text-xs font-bold text-[#E09F3E] uppercase tracking-[0.2em] mb-3"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
              transitionDelay: "0.1s",
            }}
          >
            {t.about.welcome}
          </p>

          {/* Heading with split reveal */}
          <div className="overflow-hidden">
            <h2
              className="text-4xl md:text-5xl font-serif font-light text-gray-900 dark:text-white leading-tight"
              style={{
                transform: headerVisible ? "translateY(0)" : "translateY(100%)",
                opacity: headerVisible ? 1 : 0,
                transition: "transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.8s ease",
                transitionDelay: "0.25s",
              }}
            >
              {t.about.heading} <br />
              <span className="font-normal italic">{t.about.location}</span>
            </h2>
          </div>

          {/* Decorative divider line */}
          <div className="flex justify-center mt-6 mb-4">
            <div
              className="h-[2px] bg-gradient-to-r from-transparent via-[#0F4C5C]/30 dark:via-white/20 to-transparent"
              style={{
                width: headerVisible ? "120px" : "0px",
                transition: "width 1s cubic-bezier(0.16, 1, 0.3, 1)",
                transitionDelay: "0.5s",
              }}
            />
          </div>

          {/* Subtext */}
          <p
            className="text-gray-600 dark:text-slate-300 text-base md:text-lg leading-relaxed"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.7s cubic-bezier(0.16, 1, 0.3, 1)",
              transitionDelay: "0.55s",
            }}
          >
            {t.about.desc}
          </p>
        </div>

        {/* Row 1: Image Left, Text Right (Dr. Sai Krishna) */}
        <DoctorRow
          name="Dr. Sai Krishna Kalva"
          title={t.about.drSaiTitle}
          description1={t.about.drSaiDesc1}
          description2={t.about.drSaiDesc2}
          image="/Sai.jpeg"
          alt="Dr. Sai Krishna Kalva"
          buttonText={t.about.drSaiButton}
          reversed={false}
        />

        {/* Row 2: Text Left, Image Right (Dr. Thejasri) */}
        <DoctorRow
          name="Dr. Thejasri Keerthipati"
          title={t.about.drTejaTitle}
          description1={t.about.drTejaDesc1}
          description2={t.about.drTejaDesc2}
          image="/Theju_headshot.jpg"
          alt="Dr. Thejasri Keerthipati"
          buttonText={t.about.drTejaButton}
          reversed={true}
        />

      </div>
    </section>
  );
};

export default AboutUs;
